import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut, UserButton, useSession, SignInButton, useAuth, useUser } from "@clerk/clerk-react";
import { Info, Sparkles, Activity } from 'lucide-react';
import confetti from 'canvas-confetti';
import { client } from '../lib/eden';

// ID de la extensión (inyectado vía Vite config)
const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID || 'iobnadjkbhmdoboancpnmglacknijlbk';

interface ExtensionResponse {
  success: boolean;
  alreadySynced?: boolean;
  broadcasted?: boolean;
  error?: string;
  token?: string;
}

const Header: React.FC = () => {
  const { t } = useTranslation('translation');
  const { isSignedIn } = useSession();
  const { getToken } = useAuth();
  const { user } = useUser();
  const [isOnline, setIsOnline] = useState<boolean | null>(null);

  // Fly.io Backend Health Check
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const { error } = await client.health.get();
        setIsOnline(!error);
      } catch (err) {
        setIsOnline(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Every 30 secs
    return () => clearInterval(interval);
  }, []);

  /**
   * Handles the "Try Now" click
   */
  const handleTryNow = async () => {
    console.log('[Header] 🚀 Click en botón de extensión - Iniciando sincronización manual...');
    
    // 1. Abrir el panel lateral
    window.postMessage({ type: 'OPEN_SIDE_PANEL' }, '*');
    
    // 2. Sincronizar token si hay sesión
    if (isSignedIn && user) {
      try {
        const token = await getToken();
        if (token) {
          console.log("📢 [Manual Sync] Enviando token a la extensión...");

          // Método 1: PostMessage
          window.postMessage({ 
            type: "SYNC_CLERK_SESSION", 
            token: token, 
            user: {
              id: user.id,
              email: user.primaryEmailAddress?.emailAddress,
              fullName: user.fullName,
              imageUrl: user.imageUrl,
            } 
          }, "*");

          // Método 2: Directo (si está instalada y accesible)
          if (window.chrome && window.chrome.runtime && window.chrome.runtime.sendMessage) {
            window.chrome.runtime.sendMessage(EXTENSION_ID, {
              type: 'SYNC_CLERK_SESSION',
              token,
              user: {
                id: user.id,
                email: user.primaryEmailAddress?.emailAddress,
                fullName: user.fullName,
                imageUrl: user.imageUrl,
              },
            }, (response: ExtensionResponse) => {
              if (window.chrome.runtime.lastError) {
                console.warn("⚠️ [Manual Sync] Error enviando mensaje directo:", window.chrome.runtime.lastError.message);
                return;
              }
              console.log("📢 [Manual Sync] Respuesta de extensión:", response);
            });
          }
        }
      } catch (err) {
        console.error("❌ [Manual Sync] Error:", err);
      }
    } else {
      console.log("⚠️ [Manual Sync] No hay usuario logueado, no se puede sincronizar.");
    }
    
    // Trigger confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-[#05050a]/60 backdrop-blur-xl border-b border-white/[0.05] px-6 py-4">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-6">
          <Link 
            to="/" 
            viewTransition
            className="flex items-center gap-2.5 group transition-transform hover:scale-105 min-w-0"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-blue-500 to-emerald-500 rounded-xl flex items-center justify-center font-black text-white text-sm shadow-2xl shadow-purple-500/20 group-hover:rotate-12 transition-all duration-500 shrink-0">V</div>
            <span className="font-black text-xl tracking-tighter text-white truncate">{t('common.brand_name')}</span>
          </Link>

          {/* Connection Status Indicator */}
          <div className="hidden lg:flex items-center gap-2 bg-white/[0.03] border border-white/5 px-3 py-1.5 rounded-full">
            {isOnline === null ? (
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" />
            ) : isOnline ? (
              <div className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
            ) : (
              <div className="w-2 h-2 bg-red-500 rounded-full shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
            )}
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              {isOnline === null ? t('ui.syncing') : isOnline ? `${t('ui.backend_name')} ${t('ui.status_connected')}` : `${t('ui.backend_name')} ${t('ui.status_offline')}`}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-4 md:gap-8">
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" viewTransition className="flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-white transition-colors uppercase tracking-widest whitespace-nowrap">
              <Activity className="w-3.5 h-3.5 text-purple-400 shrink-0" />
              {t('ui.stats')}
            </Link>
            <Link to="/about" viewTransition className="flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-white transition-colors uppercase tracking-widest whitespace-nowrap">
              <Info className="w-3.5 h-3.5 text-blue-400 shrink-0" />
              {t('ui.more_info')}
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={handleTryNow}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-2.5 rounded-xl text-xs font-black text-white transition-all border border-white/5 active:scale-95 whitespace-nowrap"
            >
              <Sparkles className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
              {t('ui.extension')}
            </button>

            <SignedIn>
              <div className="flex items-center gap-3 pl-4 border-l border-white/5">
                <UserButton afterSignOutUrl="/" appearance={{
                  elements: {
                    userButtonAvatarBox: "w-9 h-9 border border-white/10 hover:border-purple-500/50 transition-colors"
                  }
                }} />
              </div>
            </SignedIn>
            
            <SignedOut>
              <div className="flex items-center gap-3">
          <span className="hidden sm:inline-block ml-2 px-2 py-0.5 rounded-full bg-cyan-900/30 border border-cyan-500/30 text-[10px] font-mono text-cyan-400">
            {t('ui.ram_mode')}
          </span>
        <SignInButton mode="modal">
          <button className="bg-white text-black px-6 py-2.5 rounded-xl text-xs font-black hover:bg-gray-200 transition-all active:scale-95 uppercase">
            {t('ui.sign_in')}
          </button>
        </SignInButton>
      </div>
            </SignedOut>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
